import React from 'react';
import {
  Button,
  Card,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import { runtimeHistoryService } from '../../services/runtimeOperationalService';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

const STATUS_COLOR = {
  success: 'green',
  completed: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric} ms`;
};

const getStatusTag = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const PlatformRuntimeSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const correlationFromUrl = React.useMemo(
    () => String(searchParams.get('correlation_id') || '').trim(),
    [searchParams]
  );

  const pageFromUrl = React.useMemo(() => {
    const raw = Number(searchParams.get('page') || '1');
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
  }, [searchParams]);

  const pageSizeFromUrl = React.useMemo(() => {
    const raw = Number(searchParams.get('page_size') || '20');
    if (!Number.isFinite(raw)) {
      return 20;
    }
    const normalized = Math.floor(raw);
    if (normalized < 1) return 20;
    if (normalized > 200) return 200;
    return normalized;
  }, [searchParams]);

  const [draftCorrelation, setDraftCorrelation] = React.useState(correlationFromUrl);

  React.useEffect(() => {
    setDraftCorrelation(correlationFromUrl);
  }, [correlationFromUrl]);

  const queryParams = React.useMemo(
    () => ({
      correlation_id: correlationFromUrl,
      limit: pageSizeFromUrl,
      offset: (pageFromUrl - 1) * pageSizeFromUrl,
    }),
    [correlationFromUrl, pageFromUrl, pageSizeFromUrl]
  );

  const {
    data: searchResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsSearch(queryParams),
    enabled: Boolean(correlationFromUrl),
    queryFn: async () => {
      const response = await runtimeHistoryService.searchExecutionsByCorrelationId(queryParams);
      return response.data;
    },
  });

  const executionItems = Array.isArray(searchResponse?.items) ? searchResponse.items : [];
  const total = Number(searchResponse?.total || 0);

  const applySearch = () => {
    const normalized = String(draftCorrelation || '').trim();
    if (!normalized) {
      setSearchParams({});
      return;
    }
    setSearchParams({
      correlation_id: normalized,
      page: '1',
      page_size: String(pageSizeFromUrl),
    });
  };

  const clearSearch = () => {
    setDraftCorrelation('');
    setSearchParams({});
  };

  const handlePaginationChange = (nextPage, nextPageSize) => {
    if (!correlationFromUrl) {
      return;
    }

    setSearchParams({
      correlation_id: correlationFromUrl,
      page: String(nextPage),
      page_size: String(nextPageSize || pageSizeFromUrl),
    });
  };

  const columns = [
    {
      title: 'Execution ID',
      dataIndex: 'execution_id',
      key: 'execution_id',
      render: (value) => value || '-',
    },
    {
      title: 'Correlation ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value) => <RuntimeCorrelationChip correlationId={value} />,
    },
    {
      title: 'Operation',
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (value) => value || '-',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => getStatusTag(value),
    },
    {
      title: 'Started At',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value) => formatDuration(value),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          onClick={() =>
            navigate(
              `/platform/saas-billing/runtime/history/executions/${encodeURIComponent(
                String(record.execution_id || '')
              )}`
            )
          }
        >
          Ver detalhe
        </Button>
      ),
    },
  ];

  const apiError = isError
    ? buildApiErrorMessage(error, 'Nao foi possivel carregar resultados de correlacao.')
    : null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Runtime Correlation Search
      </Title>
      <Text type="secondary">
        Busca read-only de execucoes por correlation_id. Este endpoint nao suporta filtros de
        tenant/status/operation_type.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Space wrap size={12}>
          <Input
            placeholder="Correlation ID"
            value={draftCorrelation}
            onChange={(event) => setDraftCorrelation(event.target.value)}
            onPressEnter={applySearch}
            style={{ minWidth: 320 }}
          />

          <Button type="primary" onClick={applySearch}>
            Buscar
          </Button>

          <Button onClick={clearSearch}>Limpar</Button>

          <Button onClick={() => refetch()} loading={isFetching} disabled={!correlationFromUrl}>
            Atualizar
          </Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }}>
        {!correlationFromUrl ? (
          <RuntimeEmptyState
            title="Informe um correlation_id"
            description="Digite um correlation_id para executar a busca operacional."
          />
        ) : null}

        {correlationFromUrl && isLoading ? (
          <RuntimeLoadingState tip="Carregando resultados por correlation_id..." />
        ) : null}

        {correlationFromUrl && !isLoading && isError ? (
          <RuntimeErrorState
            title="Erro ao carregar Runtime Search"
            description={apiError?.message || 'Falha inesperada ao consultar correlacao.'}
            onRetry={() => refetch()}
          />
        ) : null}

        {correlationFromUrl && !isLoading && !isError && executionItems.length === 0 ? (
          <RuntimeEmptyState
            title="Nenhuma execucao encontrada"
            description="Nao existem execucoes para o correlation_id informado."
          />
        ) : null}

        {correlationFromUrl && !isLoading && !isError && executionItems.length > 0 ? (
          <Table
            rowKey="execution_id"
            columns={columns}
            dataSource={executionItems}
            pagination={{
              current: pageFromUrl,
              pageSize: pageSizeFromUrl,
              total,
              showSizeChanger: true,
              onChange: handlePaginationChange,
            }}
          />
        ) : null}
      </Card>
    </div>
  );
};

export default PlatformRuntimeSearch;
